'use client'

import { useMemo, useState } from 'react'
import { useRouter } from 'next/navigation'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import type { z } from 'zod'
import { Briefcase, UserPlus, Users } from 'lucide-react'
import { SERVICE_TYPE_LABEL, StatCard, type ServiceType } from '@/features/cases'
import {
  createInvestorSchema,
  useCreateInvestor,
  useInvestors,
  type CreateInvestorFormValues,
  type InvestorServiceFilterValue,
} from '@/features/investors'
import { useDebouncedValue } from '@/hooks/useDebouncedValue'
import { formatDate } from '@/lib/format/date'
import { ALL_FILTER_VALUE, TableSearch } from '@/components/patterns/table-filter-tabs'
import { TableFilterSelect } from '@/components/patterns/table-filter-select'
import { Pagination } from '@/components/patterns/pagination'
import { QueryErrorState } from '@/components/patterns/queryErrorState'
import { ListSkeleton } from '@/components/patterns/listSkeleton'
import { EmptyState } from '@/components/patterns/emptyState'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { Card, CardContent } from '@/components/ui/card'
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog'
import { Field, FieldError, FieldGroup, FieldLabel } from '@/components/ui/field'
import { Input } from '@/components/ui/input'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { AdminShell } from '@/features/admin/components/admin-shell'

const PAGE_SIZE = 20

const ADD_INVESTOR_DEFAULTS: CreateInvestorFormValues = {
  name: '',
  email: '',
  mobile: '',
  pan: '',
  city: '',
}

/** `none` is not a real `ServiceType` — it means "this investor has no case at all". */
const NO_CASE_VALUE = 'none'
type ServiceFilter = typeof ALL_FILTER_VALUE | ServiceType | typeof NO_CASE_VALUE

/** Fixed display order/labels for the dropdown — `build_portfolio` is reserved/unimplemented but
 * shown anyway (even at a real 0 count), same reasoning as Admin/Staff Calls always showing
 * "Completed" — a visible 0 is real information, not noise to hide. */
const SERVICE_FILTER_ORDER: InvestorServiceFilterValue[] = [
  'portfolio_audit',
  'trades_audit',
  'build_portfolio',
  'none',
]

const SERVICE_FILTER_LABEL: Record<InvestorServiceFilterValue, string> = {
  portfolio_audit: SERVICE_TYPE_LABEL.portfolio_audit,
  trades_audit: SERVICE_TYPE_LABEL.trades_audit,
  build_portfolio: 'Build My Portfolio',
  none: 'No case yet',
}

function AddInvestorDialog() {
  const createInvestor = useCreateInvestor()
  const [open, setOpen] = useState(false)
  const form = useForm<z.input<typeof createInvestorSchema>, unknown, CreateInvestorFormValues>({
    resolver: zodResolver(createInvestorSchema),
    mode: 'onTouched',
    defaultValues: ADD_INVESTOR_DEFAULTS,
  })

  const onSubmit = form.handleSubmit(async (values) => {
    await createInvestor.mutateAsync({ ...values, city: values.city ?? '' })
    form.reset(ADD_INVESTOR_DEFAULTS)
    setOpen(false)
  })

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button>
          <UserPlus className="size-4" /> Add Investor
        </Button>
      </DialogTrigger>
      <DialogContent className="max-h-[85vh] overflow-y-auto">
        <form onSubmit={onSubmit}>
          <DialogHeader>
            <DialogTitle>Add a new investor</DialogTitle>
            <DialogDescription>
              Create an account on their behalf. They&apos;ll sign in with this phone number or
              email.
            </DialogDescription>
          </DialogHeader>

          <FieldGroup className="py-4">
            <Field>
              <FieldLabel htmlFor="inv-name">Full name</FieldLabel>
              <Input id="inv-name" placeholder="e.g. Harsh Sharma" {...form.register('name')} />
              <FieldError errors={[form.formState.errors.name]} />
            </Field>
            <Field>
              <FieldLabel htmlFor="inv-email">Email</FieldLabel>
              <Input
                id="inv-email"
                type="email"
                placeholder="you@example.com"
                {...form.register('email')}
              />
              <FieldError errors={[form.formState.errors.email]} />
            </Field>
            <Field>
              <FieldLabel htmlFor="inv-mobile">Mobile number</FieldLabel>
              <Input
                id="inv-mobile"
                type="tel"
                inputMode="numeric"
                maxLength={10}
                placeholder="e.g. 9511874029"
                {...form.register('mobile')}
                onChange={(e) =>
                  form.setValue('mobile', e.target.value.replace(/\D/g, '').slice(0, 10), {
                    shouldValidate: true,
                  })
                }
              />
              <FieldError errors={[form.formState.errors.mobile]} />
            </Field>
            <Field>
              <FieldLabel htmlFor="inv-pan">PAN (optional)</FieldLabel>
              <Input
                id="inv-pan"
                maxLength={10}
                placeholder="e.g. ABCPS1234D"
                {...form.register('pan', {
                  onChange: (e) => {
                    e.target.value = e.target.value.replace(/[^a-zA-Z0-9]/g, '').toUpperCase()
                  },
                })}
              />
              <FieldError errors={[form.formState.errors.pan]} />
            </Field>
            <Field>
              <FieldLabel htmlFor="inv-city">City</FieldLabel>
              <Input id="inv-city" placeholder="e.g. Nagpur" {...form.register('city')} />
            </Field>
          </FieldGroup>

          <DialogFooter>
            <DialogClose asChild>
              <Button type="button" variant="outline">
                Cancel
              </Button>
            </DialogClose>
            <Button type="submit" disabled={createInvestor.isPending}>
              {createInvestor.isPending ? 'Creating…' : 'Create investor'}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  )
}

export function AdminInvestorsView() {
  const router = useRouter()
  const [serviceFilter, setServiceFilter] = useState<ServiceFilter>(ALL_FILTER_VALUE)
  const [search, setSearch] = useState('')
  const [page, setPage] = useState(1)
  const debouncedSearch = useDebouncedValue(search, 300)

  const serviceType =
    serviceFilter === ALL_FILTER_VALUE || serviceFilter === NO_CASE_VALUE
      ? undefined
      : serviceFilter
  const hasCase = serviceFilter === NO_CASE_VALUE ? false : undefined

  const { data, isLoading, isError, error, refetch } = useInvestors({
    page,
    pageSize: PAGE_SIZE,
    search: debouncedSearch || undefined,
    serviceType,
    hasCase,
  })
  const investors = data?.items ?? []
  const total = data?.total ?? 0
  const totalPages = data?.totalPages ?? 1
  const counts = data?.counts

  // Real counts from the backend, not a bare label list — unlike `admin-cases-view.tsx`'s status
  // dropdown, zero-count options are NOT filtered out here (`build_portfolio` should stay visible
  // at 0 — see `SERVICE_FILTER_ORDER`'s comment above).
  const serviceOptions = useMemo(
    () =>
      SERVICE_FILTER_ORDER.map((value) => ({
        value,
        label: SERVICE_FILTER_LABEL[value],
        count: counts?.serviceCounts.find((c) => c.value === value)?.count ?? 0,
      })),
    [counts],
  )

  return (
    <AdminShell
      active="investors"
      title="Investors"
      description="Every investor onboarded to the platform. Add new investors here — there is no public sign-up yet."
      actions={<AddInvestorDialog />}
    >
      <div className="@xl/main:grid-cols-3 grid grid-cols-1 gap-4">
        <StatCard
          label="Total Investors"
          value={counts?.total ?? 0}
          footnote="Onboarded to date"
          icon={<Users />}
          tint="var(--chart-1)"
        />
        <StatCard
          label="Added Recently"
          value={counts?.addedLast30Days ?? 0}
          trend="Last 30 days"
          trendUp
          footnote="New investor accounts"
          icon={<UserPlus />}
          tint="var(--chart-2)"
        />
        <StatCard
          label="With Active Case"
          value={counts?.withActiveCase ?? 0}
          footnote="Have started a service"
          icon={<Briefcase />}
          tint="var(--chart-5)"
        />
      </div>

      <Card>
        <CardContent className="flex flex-col gap-4">
          <div className="@lg/main:flex-row @lg/main:items-center @lg/main:justify-between flex flex-col gap-3">
            <TableFilterSelect
              value={serviceFilter}
              onValueChange={(v) => {
                setServiceFilter(v)
                setPage(1)
              }}
              allCount={counts?.total ?? 0}
              options={serviceOptions}
            />
            <TableSearch
              value={search}
              onChange={(v) => {
                setSearch(v)
                setPage(1)
              }}
              placeholder="Search name or email…"
              count={{ shown: investors.length, total, noun: 'investors' }}
            />
          </div>

          {isError ? (
            <QueryErrorState error={error} onRetry={() => void refetch()} />
          ) : isLoading ? (
            <ListSkeleton rows={6} />
          ) : investors.length === 0 ? (
            <EmptyState
              title={total === 0 ? 'No investors yet.' : 'No investors match this filter.'}
            />
          ) : (
            <>
              <div className="overflow-x-auto">
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Name</TableHead>
                      <TableHead>Service</TableHead>
                      <TableHead>Contact</TableHead>
                      <TableHead>PAN</TableHead>
                      <TableHead>City</TableHead>
                      <TableHead className="text-right">Added</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {investors.map((inv) => (
                      <TableRow
                        key={inv.id}
                        className="cursor-pointer"
                        onClick={() => router.push(`/admin/clients/${inv.id}`)}
                      >
                        <TableCell className="font-medium">{inv.name ?? '—'}</TableCell>
                        <TableCell>
                          <Badge variant={inv.caseCount > 0 ? 'default' : 'secondary'}>
                            {inv.caseCount === 0
                              ? 'No case yet'
                              : `${inv.caseCount} case${inv.caseCount === 1 ? '' : 's'}`}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          <div className="flex flex-col">
                            <span>{inv.email ?? '—'}</span>
                            <span className="text-xs">+91 {inv.mobile}</span>
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground font-mono text-xs">
                          {inv.pan || '—'}
                        </TableCell>
                        <TableCell className="text-muted-foreground">{inv.city || '—'}</TableCell>
                        <TableCell className="text-muted-foreground text-right tabular-nums">
                          {formatDate(inv.createdAt)}
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </div>
              <Pagination
                page={page}
                pageSize={PAGE_SIZE}
                total={total}
                totalPages={totalPages}
                onPageChange={setPage}
              />
            </>
          )}
        </CardContent>
      </Card>
    </AdminShell>
  )
}
